import React, { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { tokens } from '../../../../theme/tokens';
import { StagedSourceBadge } from '../../../StagedSourceBadge';
import { ChipList, DetailCard, Field, FieldGrid, FieldLabel, LinkButton, RemoveX, SelectInput, TabBody, TextInput } from '../primitives';
import type { ProductDraft } from '../productDraft';
import type { AdminCanonicalProduct } from '../productDetailTypes';
import { fieldSource, formatDate, hostOf, htmlToParagraphs, ProvenanceKey } from '../productDetailFormat';

interface Props {
  product: AdminCanonicalProduct;
  draft: ProductDraft;
  base: ProductDraft;
  editing: boolean;
  update: (patch: Partial<ProductDraft>) => void;
  startEditing: () => void;
  toast: (message: string) => void;
  /** A store or vendor editing its own submitted product (MOT-695): Motzu's pro-site fields are left out. */
  submitter?: boolean;
}

const SHORT_LIMIT = 160;

export const ContentTab: React.FC<Props> = ({ product, draft, base, editing, update, startEditing, toast, submitter = false }) => {
  const [comparing, setComparing] = useState(false);
  const src = (key: keyof ProductDraft, path: string, provenanceKey?: ProvenanceKey) =>
    fieldSource(product, draft[key], base[key], path, provenanceKey);
  const scraped = product.scrapedDescription?.trim();

  return (
    <TabBody>
      <DetailCard
        title="Copy"
        note="Shown on the store product page"
        headerAlign="center"
        actions={
          <Button
            size="small"
            variant="outlined"
            onClick={() => setComparing((c) => !c)}
            disabled={!scraped}
            title={scraped ? undefined : 'No scraped description is stored for this product'}
            sx={{
              height: 28,
              borderRadius: '7px',
              fontSize: 12,
              fontWeight: 600,
              ...(comparing ? { bgcolor: tokens.accent.tint, borderColor: tokens.accent.border, color: tokens.accent.hover } : { color: tokens.ink.body, borderColor: tokens.line.default }),
            }}
          >
            Compare with scraped
          </Button>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, px: '18px', pt: 1, pb: '18px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <FieldLabel>Product name</FieldLabel>
              <StagedSourceBadge source={src('name', 'name')} />
            </Stack>
            {editing ? (
              <TextInput ariaLabel="Product name" value={draft.name} onChange={(name) => update({ name })} />
            ) : (
              <Typography sx={{ fontSize: 14, color: tokens.ink.primary }}>{draft.name || '—'}</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <FieldLabel>Short description</FieldLabel>
              <StagedSourceBadge source={src('shortDescription', 'shortDescription', 'shortDescription')} />
              <Typography
                component="span"
                sx={{ fontSize: 11.5, color: draft.shortDescription.length > SHORT_LIMIT ? tokens.signal.caution.ink : tokens.ink.faint }}
              >
                {draft.shortDescription.length} / {SHORT_LIMIT}
              </Typography>
            </Stack>
            {editing ? (
              <TextInput ariaLabel="Short description" multiline rows={2} value={draft.shortDescription} onChange={(shortDescription) => update({ shortDescription })} />
            ) : (
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: draft.shortDescription ? tokens.ink.body : tokens.ink.faint }}>
                {draft.shortDescription || '—'}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <FieldLabel>Description</FieldLabel>
                <StagedSourceBadge source={src('description', 'description', 'description')} />
              </Stack>
              {editing ? (
                <TextInput ariaLabel="Description" multiline rows={9} value={draft.description} onChange={(description) => update({ description })} />
              ) : (
                <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: draft.description ? tokens.ink.body : tokens.ink.faint, whiteSpace: 'pre-line' }}>
                  {htmlToParagraphs(draft.description) || '—'}
                </Typography>
              )}
            </Box>
            {comparing && scraped && (
              <Box
                sx={{
                  flex: '1 1 320px',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.6,
                  px: '14px',
                  py: 1.5,
                  border: `1px dashed ${tokens.ink.disabled}`,
                  borderRadius: `${tokens.radius.control}px`,
                  bgcolor: tokens.surface.sunken,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  <FieldLabel>scrapedDescription</FieldLabel>
                  <Typography component="span" sx={{ fontSize: 11.5, color: tokens.ink.faint }}>
                    {[hostOf(product.source?.sourceUrl), formatDate(product.provenance?.description?.at ?? product.updatedAt)].filter(Boolean).join(' · ')}
                  </Typography>
                  <LinkButton
                    sx={{ ml: 'auto !important', fontSize: 12 }}
                    onClick={() => {
                      if (!editing) startEditing();
                      update({ description: scraped });
                      toast('Scraped copy placed in the editor');
                    }}
                  >
                    Use this copy
                  </LinkButton>
                </Stack>
                <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: tokens.ink.bodyAlt, whiteSpace: 'pre-line' }}>{htmlToParagraphs(scraped)}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DetailCard>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ flex: '1 1 380px', minWidth: 0 }}>
          <DetailCard title="Key features" count={draft.features.length}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: '18px', pt: 0.75, pb: 2 }}>
              {draft.features.length === 0 && !editing && <Typography sx={{ fontSize: 13, color: tokens.ink.faint }}>No features yet.</Typography>}
              {draft.features.map((feature, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.1, alignItems: 'center', fontSize: 13.5, lineHeight: 1.5, color: tokens.ink.body }}>
                  <CheckIcon sx={{ fontSize: 12, color: tokens.ink.faint, flex: '0 0 auto' }} />
                  {editing ? (
                    <>
                      <TextInput
                        ariaLabel={`Feature ${i + 1}`}
                        value={feature}
                        onChange={(value) => update({ features: draft.features.map((f, j) => (j === i ? value : f)) })}
                      />
                      <RemoveX label={`Remove feature ${i + 1}`} onClick={() => update({ features: draft.features.filter((_, j) => j !== i) })} />
                    </>
                  ) : (
                    <span>{feature}</span>
                  )}
                </Box>
              ))}
              {editing && (
                <LinkButton sx={{ alignSelf: 'flex-start' }} onClick={() => update({ features: [...draft.features, ''] })}>
                  + Add feature
                </LinkButton>
              )}
            </Box>
          </DetailCard>
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <DetailCard title="In the box" count={draft.inTheBox.length}>
            <Box sx={{ display: 'flex', flexDirection: 'column', px: '18px', pt: 0.5, pb: 1.75 }}>
              {draft.inTheBox.length === 0 && !editing && <Typography sx={{ fontSize: 13, color: tokens.ink.faint, py: 1 }}>Nothing listed yet.</Typography>}
              {draft.inTheBox.map((item, i) => (
                <Box
                  key={i}
                  sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: '44px minmax(0, 1fr) auto' }, gap: 1.25, alignItems: 'center', minHeight: 34, borderBottom: `1px solid ${tokens.line.faint}` }}
                >
                  {editing ? (
                    <>
                      <TextInput
                        ariaLabel={`Quantity of item ${i + 1}`}
                        value={item.quantity}
                        onChange={(quantity) => update({ inTheBox: draft.inTheBox.map((b, j) => (j === i ? { ...b, quantity } : b)) })}
                        sx={{ '& input': { textAlign: 'center', px: 0.5 } }}
                      />
                      <TextInput
                        ariaLabel={`Item ${i + 1}`}
                        value={item.name}
                        onChange={(name) => update({ inTheBox: draft.inTheBox.map((b, j) => (j === i ? { ...b, name } : b)) })}
                      />
                      <RemoveX label={`Remove item ${i + 1}`} onClick={() => update({ inTheBox: draft.inTheBox.filter((_, j) => j !== i) })} />
                    </>
                  ) : (
                    <>
                      <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{item.quantity} ×</Typography>
                      <Typography component="span" sx={{ fontSize: 13, color: tokens.ink.primary }}>{item.name}</Typography>
                      <span />
                    </>
                  )}
                </Box>
              ))}
              {editing && (
                <LinkButton sx={{ alignSelf: 'flex-start', mt: 1 }} onClick={() => update({ inTheBox: [...draft.inTheBox, { quantity: '1', name: '' }] })}>
                  + Add item
                </LinkButton>
              )}
            </Box>
          </DetailCard>
        </Box>
      </Box>

      <DetailCard title={submitter ? 'Tags' : 'Tags & visibility'}>
        {!submitter && (
          <FieldGrid>
            <Field
              label="Pro only"
              source={src('isProOnly', 'isProOnly')}
              display={draft.isProOnly}
              editor={editing ? <SelectInput ariaLabel="Pro only" value={draft.isProOnly} options={['Yes', 'No']} onChange={(v) => update({ isProOnly: v as 'Yes' | 'No' })} /> : undefined}
            />
            <Field
              label="Pro site URL"
              wide
              source={src('proSiteUrl', 'proSiteUrl')}
              display={draft.proSiteUrl}
              editor={editing ? <TextInput ariaLabel="Pro site URL" value={draft.proSiteUrl} placeholder="https://…" onChange={(proSiteUrl) => update({ proSiteUrl })} /> : undefined}
            />
          </FieldGrid>
        )}
        <ChipList
          label="Tags"
          hint={`${draft.tags.length} slugs`}
          items={draft.tags}
          editing={editing}
          placeholder="Add tag, press Enter"
          onChange={(tags) => update({ tags })}
        />
      </DetailCard>
    </TabBody>
  );
};
